<template>
  <!-- `as="span"` because the node is inline: it sits in the run of text the
       author is writing, exactly where the {image} token used to sit. The style
       is the shared slot geometry, so the box drawn here is laid out by the very
       declarations that are saved and later reach the screen. -->
  <NodeViewWrapper
    as="span"
    class="eip"
    :class="{ 'eip--selected': selected, 'eip--resizing': isResizing }"
    :style="boxStyle"
    contenteditable="false"
    draggable="false"
  >
    <span class="eip__face" @mousedown="selectSlot">
      <i class="pi pi-image eip__icon" aria-hidden="true"></i>
      <span class="eip__name">Photo</span>
      <span class="eip__size">{{ sizeLabel }}</span>
    </span>

    <!-- Right edge widens, bottom edge heightens, the corner does both. Placed
         with physical `right` / `bottom` rather than logical properties so the
         handle stays under the pointer in a right-to-left paragraph too, where
         dragging right must still mean wider. -->
    <span
      class="eip__handle eip__handle--e"
      title="Drag to set the width"
      @mousedown.stop.prevent
      @pointerdown="startResize($event, 'e')"
    ></span>
    <span
      class="eip__handle eip__handle--s"
      title="Drag to set the height"
      @mousedown.stop.prevent
      @pointerdown="startResize($event, 's')"
    ></span>
    <span
      class="eip__handle eip__handle--se"
      title="Drag to size the photo"
      @mousedown.stop.prevent
      @pointerdown="startResize($event, 'se')"
    ></span>
  </NodeViewWrapper>
</template>

<script>
/**
 * The Event Template's photo placeholder.
 *
 * Stands in for the event's own photo while the template is being written: a box
 * the author drags to the footprint they want the photo to take. It is only ever
 * mounted by the `eventImage` node in Rich_Text_Editor.vue — the geometry it
 * draws, and the markup that node saves, both come from
 * utils/event_template_image.ts, so the placeholder cannot show one size and the
 * screen draw another.
 */
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3';
import {
  clampEventImageRatio,
  clampEventImageWidth,
  eventImageHeightPercent,
  eventImageSlotStyleObject,
} from '@/utils/event_template_image';

export default {
  name: 'Event_Image_Placeholder',
  components: { NodeViewWrapper },
  props: nodeViewProps,
  data() {
    return {
      // The size being dragged right now, held locally so a drag redraws at
      // pointer speed without writing a document transaction per mouse move —
      // which would both churn the editor and bury the author's undo history
      // under a hundred one-pixel steps. Committed once, on release.
      dragWidth: null,
      dragRatio: null,
      resize: null,
    };
  },
  computed: {
    isResizing() {
      return this.resize !== null;
    },
    width() {
      if (this.dragWidth !== null) return this.dragWidth;
      return clampEventImageWidth(this.node.attrs.width);
    },
    ratio() {
      if (this.dragRatio !== null) return this.dragRatio;
      return clampEventImageRatio(this.node.attrs.ratio);
    },
    boxStyle() {
      return eventImageSlotStyleObject(this.width, this.ratio);
    },
    // Both figures in the same unit the width is authored in — a share of the
    // artboard's width — so the two are directly comparable, and a box read off
    // here means the same thing on any screen the template is drawn on.
    sizeLabel() {
      const height = eventImageHeightPercent(this.width, this.ratio);
      return `${Math.round(this.width)}% × ${Math.round(height)}%`;
    },
  },
  methods: {
    // Clicking the box selects the node, which is what reveals the Photo group
    // in the editor's toolbar. Done explicitly rather than left to ProseMirror's
    // own click handling so the selection is the same every time, wherever
    // inside the box the pointer landed.
    selectSlot(event) {
      if (event.button !== 0) return;
      if (typeof this.getPos !== 'function') return;
      const pos = this.getPos();
      if (typeof pos !== 'number') return;

      event.preventDefault();
      this.editor.chain().focus().setNodeSelection(pos).run();
    },
    startResize(event, direction) {
      if (event.button != null && event.button !== 0) return;
      if (typeof this.getPos !== 'function') return;

      const rect = this.$el?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height) return;

      const width = this.width;
      if (!width) return;

      event.preventDefault();
      event.stopPropagation();

      this.resize = {
        direction,
        startX: event.clientX,
        startY: event.clientY,
        startWidthPx: rect.width,
        startHeightPx: rect.height,
        // The width of whatever the box's percentage resolves against, read back
        // from the percentage the browser has just resolved rather than measured
        // off a parent. That keeps the arithmetic right through the canvas's CSS
        // zoom, and through any padding the paragraph carries, without this
        // component having to know about either.
        containerPx: rect.width / (width / 100),
      };
      this.dragWidth = width;
      this.dragRatio = this.ratio;

      window.addEventListener('pointermove', this.onResizeMove);
      window.addEventListener('pointerup', this.endResize);
      window.addEventListener('pointercancel', this.endResize);
    },
    onResizeMove(event) {
      const resize = this.resize;
      if (!resize) return;

      // An edge handle moves one side and leaves the other where it is; the
      // corner moves both. The box is described by a width and a ratio, so
      // "leave the height alone" is expressed by re-deriving the ratio from the
      // height in pixels that is being kept.
      let widthPx = resize.startWidthPx;
      let heightPx = resize.startHeightPx;
      if (resize.direction.includes('e')) {
        widthPx = resize.startWidthPx + (event.clientX - resize.startX);
      }
      if (resize.direction.includes('s')) {
        heightPx = resize.startHeightPx + (event.clientY - resize.startY);
      }

      widthPx = Math.max(widthPx, 1);
      heightPx = Math.max(heightPx, 1);

      const width = clampEventImageWidth((widthPx / resize.containerPx) * 100);
      // Measured against the width that was actually accepted, not the one the
      // pointer asked for: dragging on past the 100% ceiling would otherwise go
      // on flattening the box long after it had stopped getting any wider.
      const acceptedWidthPx = (width / 100) * resize.containerPx;
      const ratio = clampEventImageRatio(acceptedWidthPx / heightPx);

      this.dragWidth = width;
      this.dragRatio = ratio;
    },
    endResize() {
      this.stopListening();
      if (!this.resize) return;

      const width = this.dragWidth;
      const ratio = this.dragRatio;
      this.resize = null;
      this.dragWidth = null;
      this.dragRatio = null;

      if (width === null || ratio === null) return;
      // A click that never moved is not an edit, and must not land an undo step.
      if (
        width === clampEventImageWidth(this.node.attrs.width) &&
        ratio === clampEventImageRatio(this.node.attrs.ratio)
      ) {
        return;
      }

      const position = typeof this.getPos === 'function' ? this.getPos() : null;
      this.updateAttributes({ width, ratio });

      // Changing the attributes replaces the node, and ProseMirror maps the
      // selection off it into a plain cursor. Putting it back leaves the box the
      // author has just been dragging selected, so the toolbar's Photo controls
      // stay where they are instead of vanishing at the end of every drag.
      if (typeof position !== 'number') return;
      const replaced = this.editor.state.doc.nodeAt(position);
      if (replaced?.type.name !== this.node.type.name) return;
      this.editor.commands.setNodeSelection(position);
    },
    stopListening() {
      window.removeEventListener('pointermove', this.onResizeMove);
      window.removeEventListener('pointerup', this.endResize);
      window.removeEventListener('pointercancel', this.endResize);
    },
  },
  beforeUnmount() {
    // A node view is torn down the moment its node is deleted — mid-drag, if the
    // author hits undo with the button still down.
    this.stopListening();
  },
};
</script>

<style scoped>
/* Sized entirely by the bound geometry; everything here is decoration, and none
   of it may take up space — an outline and an inset shadow rather than a border
   or padding, so the placeholder's box is measurably the box the photo gets. */
/* No `overflow: hidden` here on purpose: the grab handles are placed deliberately
   just outside the box, and the only thing that could spill — the face's own
   caption — is clipped by the face instead. */
.eip {
  position: relative;
  box-sizing: border-box;
  border-radius: 6px;
  outline: 1px dashed #a5b4fc;
  outline-offset: -1px;
  background: #f5f6ff;
  cursor: default;
  user-select: none;
}

.eip--selected {
  outline: 2px solid #4f46e5;
  outline-offset: -2px;
  background: #eef2ff;
}

.eip--resizing {
  outline: 2px solid #4f46e5;
  outline-offset: -2px;
}

.eip__face {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 2px;
  overflow: hidden;
  color: #4f46e5;
  line-height: 1.2;
  text-align: center;
  /* The templates here are usually written right-to-left, and "50% × 33%" must
     not be reordered into "33% × 50%" by the paragraph's direction. */
  direction: ltr;
  unicode-bidi: isolate;
}

.eip__icon {
  font-size: 15px;
}

.eip__name {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.eip__size {
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

/* Grab areas sit half outside the box so they are still catchable on a small
   one, and are drawn only once the author is looking at the placeholder. */
.eip__handle {
  position: absolute;
  opacity: 0;
  background: #4f46e5;
  border-radius: 2px;
  transition: opacity 0.12s ease;
  touch-action: none;
}

.eip:hover .eip__handle,
.eip--selected .eip__handle,
.eip--resizing .eip__handle {
  opacity: 1;
}

.eip__handle--e {
  top: 50%;
  right: -3px;
  width: 6px;
  height: 22px;
  max-height: 60%;
  transform: translateY(-50%);
  cursor: ew-resize;
}

.eip__handle--s {
  left: 50%;
  bottom: -3px;
  height: 6px;
  width: 22px;
  max-width: 60%;
  transform: translateX(-50%);
  cursor: ns-resize;
}

.eip__handle--se {
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 2px 2px 4px 2px;
  cursor: nwse-resize;
}
</style>
